/**
 * Phone Platform API Client — Type-safe functions for all phone operations
 */

import { apiClient } from './client'

// ============================================================================
// Types
// ============================================================================

export interface PhoneNumber {
  id: string
  companyId: string
  brandId: string | null
  number: string
  twilioSid: string | null
  friendlyName: string | null
  capabilities: { voice: boolean; sms: boolean; mms: boolean }
  ivrTreeId: string | null
  status: string
  createdAt: string
  updatedAt: string
}

export interface IvrNode {
  id: string
  type: 'greeting' | 'gather' | 'route_to_agent' | 'route_to_ai' | 'voicemail' | 'callback'
  label: string
  message?: string
  voice?: string
  numDigits?: number
  timeout?: number
  children?: Record<string, string>
  aiTemplateId?: string
  skillRequired?: string
}

export interface IvrTree {
  id: string
  companyId: string
  brandId: string | null
  name: string
  description: string | null
  nodes: IvrNode[]
  holidayRules: { date: string; name: string; nodeId: string }[]
  afterHoursNodeId: string | null
  businessHours: Record<string, any> | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface AiCallTemplate {
  id: string
  companyId: string
  name: string
  description: string | null
  systemPrompt: string
  availableFunctions: string[]
  successCriteria: string | null
  voiceConfig: { provider: string; voice: string; language: string }
  sttConfig: { provider: string; model: string; language: string }
  hints: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CallListItem {
  id: string
  companyId: string
  brandId: string | null
  twilioCallSid: string | null
  direction: string
  fromNumber: string
  toNumber: string
  status: string
  type: string
  customerId: string | null
  agentId: string | null
  duration: number | null
  aiTemplateId: string | null
  startedAt: string | null
  endedAt: string | null
  createdAt: string
}

export interface CallDetail extends CallListItem {
  events: CallEvent[]
  intelligence: CallIntelligenceResult[]
  recordingUrl: string | null
  recordingS3Key: string | null
  transcriptUrl: string | null
  metadata: Record<string, any>
}

export interface CallEvent {
  id: string
  callId: string
  eventType: string
  actorId: string | null
  actorType: string
  metadata: Record<string, any>
  createdAt: string
}

export interface CallIntelligenceResult {
  id: string
  callId: string
  operatorType: string
  operatorName: string | null
  result: Record<string, any>
  score: number | null
  confidence: number | null
  createdAt: string
}

export interface CallStats {
  total: number
  inbound: number
  outbound: number
  aiCalls: number
  humanCalls: number
  avgDuration: number
  completed: number
  missed: number
}

export interface SmsMessage {
  id: string
  companyId: string
  direction: string
  fromNumber: string
  toNumber: string
  body: string | null
  status: string
  triggerType: string | null
  callId: string | null
  customerId: string | null
  sentAt: string | null
  deliveredAt: string | null
  createdAt: string
}

export interface AgentStatusRecord {
  id: string
  userId: string
  status: string
  currentCallId: string | null
  lastActivityAt: string
}

export interface UserSkill {
  id: string
  userId: string
  skill: string
  proficiency: number
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: { page: number; perPage: number; total: number; totalPages: number }
}

export interface ListCallsParams {
  direction?: string
  status?: string
  type?: string
  customerId?: string
  agentId?: string
  search?: string
  from?: string
  to?: string
  page?: number
  perPage?: number
  sortBy?: string
  sortDir?: 'asc' | 'desc'
}

export interface ListSmsParams {
  direction?: string
  customerId?: string
  callId?: string
  search?: string
  from?: string
  to?: string
  page?: number
  perPage?: number
}

// ============================================================================
// Adaptive Routing Types
// ============================================================================

export interface DimensionRating {
  mu: number
  sigma: number
  samples: number
}

export interface AgentProfile {
  userId: string
  dimensions: Record<string, DimensionRating>
  overallScore: number
  totalCalls: number
  lastUpdatedAt: string
}

export interface AgentTrendPoint {
  date: string
  avgValue: number
  count: number
}

export interface LeaderboardEntry {
  userId: string
  score: number
  confidence: number
  totalCalls: number
}

export interface EnrichedLeaderboardEntry extends LeaderboardEntry {
  firstName: string
  lastName: string
  email: string
  dimensions: Record<string, DimensionRating>
}

export interface PerformanceSignalRecord {
  id: string
  userId: string
  callId: string
  dimension: string
  value: number
  confidence: number
  source: string
  createdAt: string
}

export interface TranscriptionChunk {
  text: string
  speaker: 'caller' | 'agent' | 'unknown'
  timestamp: number
  isFinal: boolean
  confidence: number
  sentiment: {
    score: number
    label: 'positive' | 'negative' | 'neutral' | 'frustrated' | 'satisfied'
    emotions: string[]
    topics: string[]
  }
}

export interface LiveCallData {
  callId: string
  chunks: TranscriptionChunk[]
  overallSentiment: {
    score: number
    label: string
    emotions: string[]
    topics: string[]
  }
  duration: number
  isActive: boolean
}

export interface ScoredAgent {
  userId: string
  adaptiveScore: number
  exploitScore: number
  exploreScore: number
  profileConfidence: number
  dimensionScores: Partial<Record<string, number>>
}

// ============================================================================
// API Functions
// ============================================================================

function toQuery(params?: Record<string, any>): Record<string, string> {
  if (!params) return {}
  const q: Record<string, string> = {}
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q[k] = String(v)
  }
  return q
}

export const phoneAPI = {
  // ── Phone Numbers ──────────────────────────────────────────────
  numbers: {
    list: () => apiClient.get<PhoneNumber[]>('/phone/numbers'),
    create: (data: Partial<PhoneNumber>) => apiClient.post<PhoneNumber>('/phone/numbers', data),
    update: (id: string, data: Partial<PhoneNumber>) => apiClient.patch<PhoneNumber>(`/phone/numbers/${id}`, data),
    delete: (id: string) => apiClient.delete(`/phone/numbers/${id}`),
  },

  // ── IVR Trees ──────────────────────────────────────────────────
  ivrTrees: {
    list: () => apiClient.get<IvrTree[]>('/phone/ivr-trees'),
    getById: (id: string) => apiClient.get<IvrTree>(`/phone/ivr-trees/${id}`),
    create: (data: Partial<IvrTree>) => apiClient.post<IvrTree>('/phone/ivr-trees', data),
    update: (id: string, data: Partial<IvrTree>) => apiClient.patch<IvrTree>(`/phone/ivr-trees/${id}`, data),
    delete: (id: string) => apiClient.delete(`/phone/ivr-trees/${id}`),
  },

  // ── AI Templates ───────────────────────────────────────────────
  aiTemplates: {
    list: () => apiClient.get<AiCallTemplate[]>('/phone/ai-templates'),
    getById: (id: string) => apiClient.get<AiCallTemplate>(`/phone/ai-templates/${id}`),
    create: (data: Partial<AiCallTemplate>) => apiClient.post<AiCallTemplate>('/phone/ai-templates', data),
    update: (id: string, data: Partial<AiCallTemplate>) => apiClient.patch<AiCallTemplate>(`/phone/ai-templates/${id}`, data),
    delete: (id: string) => apiClient.delete(`/phone/ai-templates/${id}`),
  },

  // ── Calls ──────────────────────────────────────────────────────
  calls: {
    list: (params?: ListCallsParams) => apiClient.get<PaginatedResponse<CallListItem>>('/phone/calls', toQuery(params)),
    stats: (from?: string, to?: string) => apiClient.get<CallStats>('/phone/calls/stats', toQuery({ from, to })),
    getById: (id: string) => apiClient.get<CallDetail>(`/phone/calls/${id}`),
    create: (data: { to: string; aiTemplateId?: string; customerId?: string; jobId?: string }) =>
      apiClient.post<{ id: string }>('/phone/calls', data),
    fetchRecording: (id: string) =>
      apiClient.post<{ recordingUrl: string | null; recordingSid?: string }>(`/phone/calls/${id}/fetch-recording`),
    transfer: (id: string, data: { to: string; type?: 'agent' | 'number' }) =>
      apiClient.post<{ transferred: boolean; to: string; type: string }>(`/phone/calls/${id}/transfer`, data),
    conference: (id: string, data: { addParty: string; type?: 'agent' | 'number' }) =>
      apiClient.post<{ conferenceName: string; addedPartySid: string }>(`/phone/calls/${id}/conference`, data),
    live: (id: string) =>
      apiClient.get<LiveCallData>(`/phone/calls/${id}/live`),
  },

  // ── SMS ────────────────────────────────────────────────────────
  sms: {
    list: (params?: ListSmsParams) => apiClient.get<PaginatedResponse<SmsMessage>>('/phone/sms', toQuery(params)),
    send: (data: { to: string; body: string; brandId?: string; customerId?: string }) =>
      apiClient.post<{ id: string; twilioSid: string }>('/phone/sms', data),
  },

  // ── Agent Status ───────────────────────────────────────────────
  agentStatus: {
    get: () => apiClient.get<AgentStatusRecord>('/phone/agent-status'),
    update: (status: string) => apiClient.put<{ updated: boolean }>('/phone/agent-status', { status }),
    listOnline: () => apiClient.get<AgentStatusRecord[]>('/phone/agents-online'),
  },

  // ── User Skills ────────────────────────────────────────────────
  userSkills: {
    list: (userId: string) => apiClient.get<UserSkill[]>(`/phone/user-skills/${userId}`),
    set: (userId: string, skills: { skill: string; proficiency: number }[]) =>
      apiClient.put<{ updated: boolean }>(`/phone/user-skills/${userId}`, { skills }),
  },

  // ── Voice Token (Softphone) ────────────────────────────────────
  voiceToken: () => apiClient.post<{ token: string }>('/phone/voice-token'),

  // ── Adaptive Routing ───────────────────────────────────────────
  adaptive: {
    profile: (userId?: string) =>
      apiClient.get<AgentProfile>(userId ? `/phone/adaptive/profile/${userId}` : '/phone/adaptive/profile'),
    trend: (userId: string, dimension?: string, days?: number) =>
      apiClient.get<AgentTrendPoint[]>(`/phone/adaptive/trend/${userId}`, toQuery({ dimension, days })),
    leaderboard: (dimension?: string, limit?: number) =>
      apiClient.get<LeaderboardEntry[]>('/phone/adaptive/leaderboard', toQuery({ dimension, limit })),
    scores: (skill?: string) =>
      apiClient.get<ScoredAgent[]>('/phone/adaptive/scores', toQuery({ skill })),
    dimensions: () =>
      apiClient.get<{ dimensions: string[] }>('/phone/adaptive/dimensions'),
    // Admin endpoints
    admin: {
      enrichedLeaderboard: (dimension?: string) =>
        apiClient.get<EnrichedLeaderboardEntry[]>('/phone/adaptive/admin/leaderboard', toQuery({ dimension })),
      listSignals: (userId: string, params?: { dimension?: string; page?: number; perPage?: number }) =>
        apiClient.get<PaginatedResponse<PerformanceSignalRecord>>(`/phone/adaptive/admin/signals/${userId}`, toQuery(params)),
      deleteSignal: (signalId: string) =>
        apiClient.delete<{ userId: string }>(`/phone/adaptive/admin/signals/${signalId}`),
      addSignal: (userId: string, data: { dimension: string; value: number; confidence: number; note?: string }) =>
        apiClient.post<{ id: string }>(`/phone/adaptive/admin/signals/${userId}`, data),
      overrideDimension: (userId: string, dimension: string, value: number) =>
        apiClient.put<AgentProfile>(`/phone/adaptive/admin/profile/${userId}/dimension`, { dimension, value }),
      resetProfile: (userId: string) =>
        apiClient.post<AgentProfile>(`/phone/adaptive/admin/profile/${userId}/reset`),
    },
  },
}
